import { useState } from 'react';
import { Box } from '@mui/material';
import {
  createSearchState,
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  useUserAccess,
  formListValueFormatter,
  FormSearchResultAdvanced,
  useStore,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';

import { email, enabled } from './accessorFunctions';
import { listSchema, searchSchema } from '../../schemas';
import validationSchema from './validations';

function UserList() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const userAccess = useUserAccess();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(0);
  const store = useStore();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/users?companyId=${
    userAccess?.company?.id
  }`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'USERS_LIST_BREADCRUMB',
        defaultMessage: 'List Users',
      }),
      icon: IconLibrary.Users,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_NEW_USER_STACKABLE_BUTTON',
        defaultMessage: 'Create User',
      }),
      permissionName: 'CAN_CREATE_USER',
      Icon: IconLibrary.User,
      variant: 'outlined',
      onClick: () => {
        navigate('/users/create');
      },
    },
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, status: statusArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        status: statusArg === 'true',
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('UserList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    const formatterMap = {};
    return formListValueFormatter({ data: searchedParams, formatterMap });
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'UserList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      email,
      enabled,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortBy: 'email',
    sortOrder: 'ASC',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(listProps.storeName);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    storeName: listProps.storeName,
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'enabled':
        return value ? 'Enabled' : 'Disabled';
      default:
        return value;
    }
  };

  const searchParamsProps = {
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
    storeName: listProps.storeName,
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'USERSEARCH_HEADING',
            defaultMessage: 'Search Users',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        label={formatMessage({
          id: 'USERLIST_HEADING',
          defaultMessage: 'Users',
        })}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(UserList);
